import { Component, OnInit, OnDestroy } from "@angular/core";

import { RouterModule } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faSync,
  faPlay,
  faPause,
  faTrash,
  faRedo,
  faClock,
  faServer,
  faExclamationTriangle,
  faCheck,
  faBroom,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { Subscription, interval } from "rxjs";

import { SolidQueueService } from "../../_services/solid-queue.service";
import {
  QueueOverview,
  QueueStats,
  ProcessInfo,
  FailedJob,
  FailedJobsResponse,
  RecurringTask,
} from "../../_models/solid-queue.interface";

// Dashboard-Komponente für die Solid Queue Verwaltung
@Component({
  selector: "app-solid-queue-dashboard",
  standalone: true,
  imports: [FontAwesomeModule, RouterModule],
  templateUrl: "./solid-queue-dashboard.component.html",
  styleUrls: ["./solid-queue-dashboard.component.scss"],
  host: { class: "app-page" },
})
export class SolidQueueDashboardComponent implements OnInit, OnDestroy {
  // FontAwesome Icons
  faSync = faSync;
  faPlay = faPlay;
  faPause = faPause;
  faTrash = faTrash;
  faRedo = faRedo;
  faClock = faClock;
  faServer = faServer;
  faExclamationTriangle = faExclamationTriangle;
  faCheck = faCheck;
  faBroom = faBroom;
  faBolt = faBolt;

  // Daten
  overview: QueueOverview | null = null;
  failedJobs: FailedJob[] = [];
  failedJobsTotal = 0;
  failedJobsPage = 1;
  failedJobsPerPage = 25;
  failedJobsTotalPages = 1;
  recurringTasks: RecurringTask[] = [];

  // Ladezustände
  loading = true;
  actionInProgress = false;

  // Auto-Refresh
  private refreshSubscription: Subscription | null = null;
  private readonly REFRESH_INTERVAL = 5000;

  constructor(private solidQueueService: SolidQueueService) {}

  ngOnInit(): void {
    this.loadAllData();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  // Alle Daten laden
  loadAllData(): void {
    this.loadOverview();
    this.loadFailedJobs();
    this.loadRecurringTasks();
  }

  // Übersichtsdaten laden
  private loadOverview(): void {
    this.solidQueueService.getOverview().subscribe({
      next: (data) => {
        this.overview = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  // Fehlgeschlagene Jobs laden
  private loadFailedJobs(): void {
    this.solidQueueService.getFailedJobs(this.failedJobsPage, this.failedJobsPerPage).subscribe({
      next: (data: FailedJobsResponse) => {
        this.failedJobs = data.jobs;
        this.failedJobsTotal = data.total;
        this.failedJobsTotalPages = data.total_pages;
      },
    });
  }

  // Wiederkehrende Aufgaben laden
  private loadRecurringTasks(): void {
    this.solidQueueService.getRecurringTasks().subscribe({
      next: (data) => {
        this.recurringTasks = data;
      },
    });
  }

  // Auto-Refresh starten
  private startAutoRefresh(): void {
    this.refreshSubscription = interval(this.REFRESH_INTERVAL).subscribe(() => {
      this.loadAllData();
    });
  }

  // Auto-Refresh stoppen
  private stopAutoRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = null;
    }
  }

  // Einzelnen Job erneut ausführen
  retryJob(id: number): void {
    this.actionInProgress = true;
    this.solidQueueService.retryJob(id).subscribe({
      next: () => {
        this.actionInProgress = false;
        this.loadAllData();
      },
      error: () => {
        this.actionInProgress = false;
      },
    });
  }

  // Alle fehlgeschlagenen Jobs erneut ausführen
  retryAllFailed(): void {
    if (!confirm("Alle fehlgeschlagenen Jobs wirklich erneut ausführen?")) return;
    this.actionInProgress = true;
    this.solidQueueService.retryAllFailed().subscribe({
      next: () => {
        this.actionInProgress = false;
        this.loadAllData();
      },
      error: () => {
        this.actionInProgress = false;
      },
    });
  }

  // Einzelnen Job verwerfen
  discardJob(id: number): void {
    this.actionInProgress = true;
    this.solidQueueService.discardJob(id).subscribe({
      next: () => {
        this.actionInProgress = false;
        this.loadAllData();
      },
      error: () => {
        this.actionInProgress = false;
      },
    });
  }

  // Alle fehlgeschlagenen Jobs verwerfen
  discardAllFailed(): void {
    if (!confirm("Alle fehlgeschlagenen Jobs wirklich löschen?")) return;
    this.actionInProgress = true;
    this.solidQueueService.discardAllFailed().subscribe({
      next: () => {
        this.actionInProgress = false;
        this.loadAllData();
      },
      error: () => {
        this.actionInProgress = false;
      },
    });
  }

  // Queue pausieren
  pauseQueue(queueName: string): void {
    this.actionInProgress = true;
    this.solidQueueService.pauseQueue(queueName).subscribe({
      next: () => {
        this.actionInProgress = false;
        this.loadAllData();
      },
      error: () => {
        this.actionInProgress = false;
      },
    });
  }

  // Queue fortsetzen
  resumeQueue(queueName: string): void {
    this.actionInProgress = true;
    this.solidQueueService.resumeQueue(queueName).subscribe({
      next: () => {
        this.actionInProgress = false;
        this.loadAllData();
      },
      error: () => {
        this.actionInProgress = false;
      },
    });
  }

  // Wiederkehrende Aufgabe manuell auslösen
  triggerRecurring(key: string): void {
    this.actionInProgress = true;
    this.solidQueueService.triggerRecurring(key).subscribe({
      next: () => {
        this.actionInProgress = false;
        this.loadAllData();
      },
      error: () => {
        this.actionInProgress = false;
      },
    });
  }

  // Fertige Jobs aufräumen
  clearFinished(): void {
    if (!confirm("Alle fertigen Jobs wirklich aufräumen?")) return;
    this.actionInProgress = true;
    this.solidQueueService.clearFinished().subscribe({
      next: () => {
        this.actionInProgress = false;
        this.loadAllData();
      },
      error: () => {
        this.actionInProgress = false;
      },
    });
  }

  // Seite der fehlgeschlagenen Jobs wechseln
  goToFailedPage(page: number): void {
    if (page < 1 || page > this.failedJobsTotalPages) return;
    this.failedJobsPage = page;
    this.loadFailedJobs();
  }

  // Relativen Zeitstempel berechnen (z.B. "vor 2s")
  relativeTime(dateStr: string): string {
    if (!dateStr) return "–";
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffS = Math.floor(diffMs / 1000);

    if (diffS < 60) return `vor ${diffS}s`;
    const diffM = Math.floor(diffS / 60);
    if (diffM < 60) return `vor ${diffM}min`;
    const diffH = Math.floor(diffM / 60);
    if (diffH < 24) return `vor ${diffH}h`;
    const diffD = Math.floor(diffH / 24);
    return `vor ${diffD}d`;
  }

  // Prüfen ob Heartbeat aktuell ist (weniger als 30 Sekunden)
  isHeartbeatRecent(dateStr: string): boolean {
    if (!dateStr) return false;
    const now = new Date();
    const date = new Date(dateStr);
    return now.getTime() - date.getTime() < 15000;
  }

  // Fehlermeldung kürzen
  truncateError(error: string, maxLength: number = 80): string {
    if (!error) return "–";
    return error.length > maxLength ? error.substring(0, maxLength) + "..." : error;
  }

  // Cron-Ausdruck in lesbares Format umwandeln
  humanizeCron(cron: string): string {
    if (!cron) return "–";
    const parts = cron.split(" ");
    if (parts.length < 5) return cron;

    // Häufige Muster erkennen
    if (cron === "* * * * *") return "Jede Minute";
    if (cron === "0 * * * *") return "Jede Stunde";
    if (cron === "0 0 * * *") return "Täglich um Mitternacht";
    if (parts[0] !== "*" && parts[1] === "*") return `Alle ${parts[0]} Minuten`;
    if (parts[0].startsWith("*/")) return `Alle ${parts[0].substring(2)} Minuten`;
    if (parts[1].startsWith("*/")) return `Alle ${parts[1].substring(2)} Stunden`;

    return cron;
  }

  // Seitenzahlen für Paginierung generieren
  get paginationPages(): number[] {
    const pages: number[] = [];
    for (let i = 1; i <= this.failedJobsTotalPages; i++) {
      pages.push(i);
    }
    return pages;
  }
}
